import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
  Paper,
  Grid,
  Collapse,
} from '@mui/material';
import { Notification } from '@progress/kendo-react-notification';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import * as pdfjsLib from 'pdfjs-dist';
import { authService } from '../../..';
import { httpGetAsync, httpPostAsync } from '../../../utils/HttpClient';
import { ESignSignerRole, ESignSignerRoleOptions } from './ZohoSignDialog';

// Set up pdf.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Page dimensions type
interface PageDimensions {
  width: number;  // in PDF points
  height: number; // in PDF points
}

// Field types matching the backend enum
export enum ESignFieldType {
  Signature = 1,
  Initial = 2,
  DateSigned = 3,
  TextField = 4,
  Checkbox = 5
}

export const ESignFieldTypeOptions = [
  { value: ESignFieldType.Signature, label: "Signature", color: "#2196f3" },
  { value: ESignFieldType.Initial, label: "Initials", color: "#4caf50" },
  { value: ESignFieldType.DateSigned, label: "Date Signed", color: "#ff9800" },
  { value: ESignFieldType.TextField, label: "Text Field", color: "#9c27b0" },
  { value: ESignFieldType.Checkbox, label: "Checkbox", color: "#607d8b" }
];

export interface ESignDocumentFieldDto {
  id?: string;
  loanDocumentId: string;
  signerRole: ESignSignerRole;
  fieldType: ESignFieldType;
  fieldTypeName?: string;
  fieldName?: string;
  fieldLabel?: string;
  pageNo: number;
  xCoord: number;
  yCoord: number;
  absWidth: number;
  absHeight: number;
  isMandatory: boolean;
  sortOrder: number;
  dateFormat?: string;
}

export interface PdfCoordinatePickerProps {
  loanDocumentId: string;
  documentName: string;
  onClose: () => void;
  onSaved?: () => void;
}

// Date format options for DateSigned fields
const DATE_FORMAT_OPTIONS = [
  { value: 'MM/dd/yyyy', label: 'MM/dd/yyyy (01/15/2025)' },
  { value: 'dd/MM/yyyy', label: 'dd/MM/yyyy (15/01/2025)' },
  { value: 'yyyy-MM-dd', label: 'yyyy-MM-dd (2025-01-15)' },
  { value: 'MMM dd, yyyy', label: 'MMM dd, yyyy (Jan 15, 2025)' },
  { value: 'MMMM dd, yyyy', label: 'MMMM dd, yyyy (January 15, 2025)' },
  { value: 'dd MMM yyyy', label: 'dd MMM yyyy (15 Jan 2025)' },
];

// Default field sizes
const DEFAULT_FIELD_SIZES: Record<ESignFieldType, { width: number; height: number }> = {
  [ESignFieldType.Signature]: { width: 115, height: 20 },
  [ESignFieldType.Initial]: { width: 41, height: 16 },
  [ESignFieldType.DateSigned]: { width: 100, height: 20 },
  [ESignFieldType.TextField]: { width: 100, height: 20 },
  [ESignFieldType.Checkbox]: { width: 20, height: 20 }
};

// PDF standard DPI is 72
const PDF_DPI = 72;

export default function PdfCoordinatePicker(props: PdfCoordinatePickerProps) {
  const { loanDocumentId, documentName, onClose, onSaved } = props;

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // PDF state
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [pageDimensions, setPageDimensions] = useState<Map<number, PageDimensions>>(new Map());
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [renderScale, setRenderScale] = useState(1);
  
  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderingRef = useRef(false);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const fieldItemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  
  // Field configuration state
  const [fields, setFields] = useState<ESignDocumentFieldDto[]>([]);
  const [selectedSignerRole, setSelectedSignerRole] = useState<ESignSignerRole>(ESignSignerRole.Buyer);
  const [selectedFieldType, setSelectedFieldType] = useState<ESignFieldType>(ESignFieldType.Signature);
  const [isPlacingField, setIsPlacingField] = useState(false);
  const [placementPage, setPlacementPage] = useState(1); // Page number for new field placement (1-indexed for user)
  
  // Edit mode state
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<ESignDocumentFieldDto>>({});

  // Load PDF and existing fields on mount
  useEffect(() => {
    loadPdfAndFields();
  }, [loanDocumentId]);

  const loadPdfAndFields = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const bearerToken = await authService.getToken();
      
      // Load PDF
      const pdfResponse = await httpGetAsync(
        `api/zohoSign/document/${loanDocumentId}/pdf`,
        bearerToken!
      );
      
      if (pdfResponse?.ok) {
        const pdfData = await pdfResponse.json();
        setPdfBase64(pdfData.bytes);
        
        // Parse PDF with pdf.js to get actual page dimensions
        try {
          const pdfBytes = Uint8Array.from(atob(pdfData.bytes), c => c.charCodeAt(0));
          const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
          const loadedPdfDoc = await loadingTask.promise;
          
          setPdfDoc(loadedPdfDoc);
          setTotalPages(loadedPdfDoc.numPages);
          
          // Get dimensions for each page
          const dims = new Map<number, PageDimensions>();
          for (let i = 1; i <= loadedPdfDoc.numPages; i++) {
            const page = await loadedPdfDoc.getPage(i);
            const viewport = page.getViewport({ scale: 1 }); // scale 1 = actual PDF points
            dims.set(i, { width: viewport.width, height: viewport.height });
          }
          setPageDimensions(dims);
        } catch (pdfErr) {
          console.error('Failed to parse PDF for dimensions:', pdfErr);
          // Fall back to letter size if parsing fails
          setTotalPages(1);
          setPageDimensions(new Map([[1, { width: 612, height: 792 }]]));
        }
      } else {
        setError('Failed to load document PDF');
        return;
      }
      
      // Load existing fields
      const fieldsResponse = await httpGetAsync(
        `api/zohoSign/document/${loanDocumentId}/fields`,
        bearerToken!
      );
      
      if (fieldsResponse?.ok) {
        const existingFields: ESignDocumentFieldDto[] = await fieldsResponse.json();
        setFields(existingFields);
      }
    } catch (err) {
      setError('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  // Render a specific page to the canvas
  const renderPage = useCallback(async (pageNum: number) => {
    if (!pdfDoc || !canvasRef.current || renderingRef.current) return;
    
    renderingRef.current = true;
    try {
      const page = await pdfDoc.getPage(pageNum);
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      if (!context) return;

      // Get container width for scaling
      const containerWidth = pdfContainerRef.current?.clientWidth ?? 600;
      const originalViewport = page.getViewport({ scale: 1 });
      
      // Scale to fit container width
      const scale = (containerWidth - 20) / originalViewport.width; // -20 for padding
      setRenderScale(scale);
      
      const viewport = page.getViewport({ scale });
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;
    } finally {
      renderingRef.current = false;
    }
  }, [pdfDoc]);

  // Re-render when current page changes
  useEffect(() => {
    if (pdfDoc && currentPage >= 1 && currentPage <= totalPages) {
      renderPage(currentPage);
    }
  }, [pdfDoc, currentPage, totalPages, renderPage]);

  // Ensure initial render happens after canvas is mounted
  // Use a small delay to ensure the canvas ref is available
  useEffect(() => {
    if (pdfDoc && !loading) {
      const timer = setTimeout(() => {
        renderPage(currentPage);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [pdfDoc, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync placement page with current page for viewing
  useEffect(() => {
    setCurrentPage(placementPage);
  }, [placementPage]);

  const handlePdfClick = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isPlacingField || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Get click position relative to canvas
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    
    // Convert screen pixels to PDF points using the render scale
    // renderScale is (canvasPixels / pdfPoints), so we divide to get PDF points
    const xCoord = Math.round(clickX / renderScale);
    const yCoord = Math.round(clickY / renderScale);
    
    const fieldDefaults = DEFAULT_FIELD_SIZES[selectedFieldType];
    
    // Use currentPage (1-indexed) - 1 for 0-indexed storage (ZohoSign expects 0-indexed)
    const pageNo = Math.max(0, currentPage - 1);
    
    const newField: ESignDocumentFieldDto = {
      loanDocumentId,
      signerRole: selectedSignerRole,
      fieldType: selectedFieldType,
      pageNo,
      xCoord,
      yCoord,
      absWidth: fieldDefaults.width,
      absHeight: fieldDefaults.height,
      isMandatory: true,
      sortOrder: fields.length,
      dateFormat: selectedFieldType === ESignFieldType.DateSigned ? 'MM/dd/yyyy' : undefined
    };
    
    setFields(prev => [...prev, newField]);
    setIsPlacingField(false);
    setSuccess(`Added ${ESignFieldTypeOptions.find(o => o.value === selectedFieldType)?.label} field for ${ESignSignerRoleOptions.find(o => o.value === selectedSignerRole)?.label} on page ${currentPage}`);
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(null), 3000);
  }, [isPlacingField, selectedSignerRole, selectedFieldType, currentPage, loanDocumentId, fields.length, pageDimensions, renderScale]);

  const handleRemoveField = (index: number) => {
    setFields(prev => prev.filter((_, i) => i !== index));
    // If we were editing this field, close the editor
    if (editingFieldIndex === index) {
      setEditingFieldIndex(null);
      setEditFormData({});
    } else if (editingFieldIndex !== null && editingFieldIndex > index) {
      // Adjust editing index if a field before it was removed
      setEditingFieldIndex(editingFieldIndex - 1);
    }
  };

  const handleEditField = (index: number) => {
    if (editingFieldIndex === index) {
      // Toggle off
      setEditingFieldIndex(null);
      setEditFormData({});
    } else {
      setEditingFieldIndex(index);
      setEditFormData({ ...fields[index] });
      
      // Scroll to the field item in the list after a short delay for the DOM to update
      setTimeout(() => {
        const element = fieldItemRefs.current.get(index);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    }
  };

  const handleEditFormChange = (field: keyof ESignDocumentFieldDto, value: any) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
    
    // Apply changes live to see overlay update in real-time
    if (editingFieldIndex !== null) {
      setFields(prev => {
        const updated = [...prev];
        updated[editingFieldIndex] = {
          ...updated[editingFieldIndex],
          [field]: value,
        };
        // Set default dateFormat when changing to DateSigned
        if (field === 'fieldType' && value === ESignFieldType.DateSigned && !updated[editingFieldIndex].dateFormat) {
          updated[editingFieldIndex].dateFormat = 'MM/dd/yyyy';
        }
        return updated;
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingFieldIndex(null);
    setEditFormData({});
  };

  const handleSaveFields = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const bearerToken = await authService.getToken();
      const response = await httpPostAsync(
        `api/zohoSign/document/${loanDocumentId}/fields`,
        { fields },
        bearerToken!
      );
      
      if (response?.ok) {
        const result = await response.json();
        if (result.success) {
          setSuccess('Field configurations saved successfully!');
          setFields(result.fields);
          onSaved?.();
        } else {
          setError('Failed to save field configurations');
        }
      } else {
        const errorText = await response?.text();
        setError(errorText || 'Failed to save field configurations');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save field configurations');
    } finally {
      setSaving(false);
    }
  };

  const getFieldColor = (fieldType: ESignFieldType) => {
    return ESignFieldTypeOptions.find(o => o.value === fieldType)?.color || '#2196f3';
  };

  const getSignerRoleLabel = (role: ESignSignerRole) => {
    return ESignSignerRoleOptions.find(o => o.value === role)?.label || 'Unknown';
  };

  const getFieldTypeLabel = (fieldType: ESignFieldType) => {
    return ESignFieldTypeOptions.find(o => o.value === fieldType)?.label || 'Unknown';
  };

  if (loading) {
    return (
      <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Configure Signature Fields - {documentName}</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Configure Signature Fields - {documentName}</DialogTitle>
      <DialogContent>
        {error && (
          <Notification type={{ style: 'error', icon: true }} closable={true} onClose={() => setError(null)} style={{ marginBottom: 16 }}>
            {error}
          </Notification>
        )}
        
        {success && (
          <Notification type={{ style: 'success', icon: true }} closable={true} onClose={() => setSuccess(null)} style={{ marginBottom: 16 }}>
            {success}
          </Notification>
        )}

        <Grid container spacing={2}>
          {/* Left panel - PDF viewer with canvas rendering */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 1, height: '600px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Page navigation */}
              <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setPlacementPage(p => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                  >
                    ← Prev
                  </Button>
                  <Typography variant="body2">
                    Page {currentPage} of {totalPages}
                  </Typography>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setPlacementPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    Next →
                  </Button>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {(() => {
                    const dims = pageDimensions.get(currentPage);
                    if (dims) {
                      const widthInches = (dims.width / 72).toFixed(2);
                      const heightInches = (dims.height / 72).toFixed(2);
                      return `${widthInches}" × ${heightInches}" (${dims.width}×${dims.height} pts)`;
                    }
                    return '';
                  })()}
                </Typography>
              </Box>
              
              <Box 
                ref={pdfContainerRef}
                sx={{ 
                  position: 'relative', 
                  flex: 1,
                  border: '1px solid #ccc',
                  overflow: 'auto',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                }}
              >
                {pdfDoc && (
                  <Box sx={{ position: 'relative' }}>
                    {/* PDF canvas rendered by pdf.js */}
                    <canvas
                      ref={canvasRef}
                      onClick={isPlacingField ? handlePdfClick : undefined}
                      style={{ 
                        cursor: isPlacingField ? 'crosshair' : 'default',
                        display: 'block',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        backgroundColor: 'white',
                      }}
                    />
                    
                    {/* Field markers overlay */}
                    {fields.map((field, actualIndex) => {
                      // Only show fields on current page (pageNo is 0-indexed)
                      if (field.pageNo !== currentPage - 1) return null;
                      
                      const fieldTypeOption = ESignFieldTypeOptions.find(o => o.value === field.fieldType);
                      const signerOption = ESignSignerRoleOptions.find(o => o.value === field.signerRole);
                      const color = fieldTypeOption?.color ?? '#2196f3';
                      const isSelected = editingFieldIndex === actualIndex;
                      
                      // Convert PDF coordinates to screen pixels using renderScale
                      const left = field.xCoord * renderScale;
                      const top = field.yCoord * renderScale;
                      const width = field.absWidth * renderScale;
                      const height = field.absHeight * renderScale;
                      
                      return (
                        <Box
                          key={`field-marker-${actualIndex}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isPlacingField) {
                              handleEditField(actualIndex);
                            }
                          }}
                          sx={{
                            position: 'absolute',
                            left: `${left}px`,
                            top: `${top}px`,
                            width: `${width}px`,
                            height: `${height}px`,
                            border: isSelected ? `3px solid ${color}` : `2px solid ${color}`,
                            backgroundColor: isSelected ? `${color}55` : `${color}33`,
                            pointerEvents: isPlacingField ? 'none' : 'auto',
                            cursor: isPlacingField ? 'crosshair' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            color: color,
                            fontWeight: 'bold',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            boxShadow: isSelected ? `0 0 8px ${color}` : 'none',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              backgroundColor: `${color}44`,
                              boxShadow: `0 0 4px ${color}`,
                            },
                          }}
                          title={`Click to edit: ${fieldTypeOption?.label ?? 'Field'} - ${signerOption?.label ?? 'Signer'} (${field.xCoord}, ${field.yCoord})`}
                        >
                          {width > 30 && height > 12 && (
                            <span style={{ textShadow: '0 0 2px white' }}>
                              {(signerOption?.label ?? 'S')[0]}{actualIndex + 1}
                            </span>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {!pdfDoc && !loading && (
                  <Typography color="textSecondary" sx={{ mt: 4 }}>
                    Failed to load PDF
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Right panel - Field configuration */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '600px', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>
                Field Configuration
              </Typography>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* Field type and signer role selectors */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Signer Role</InputLabel>
                <Select
                  value={selectedSignerRole}
                  label="Signer Role"
                  onChange={(e) => setSelectedSignerRole(e.target.value as ESignSignerRole)}
                >
                  {ESignSignerRoleOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Field Type</InputLabel>
                <Select
                  value={selectedFieldType}
                  label="Field Type"
                  onChange={(e) => setSelectedFieldType(e.target.value as ESignFieldType)}
                >
                  {ESignFieldTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, backgroundColor: option.color, borderRadius: '2px' }} />
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Page number input - syncs with PDF display */}
              <TextField
                label="Go to Page"
                type="number"
                value={placementPage}
                onChange={(e) => setPlacementPage(Math.max(1, Math.min(totalPages, parseInt(e.target.value) || 1)))}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
                InputProps={{ inputProps: { min: 1, max: totalPages } }}
                helperText={`Page ${placementPage} of ${totalPages} - Fields will be placed on displayed page`}
              />

              <Button
                variant={isPlacingField ? "contained" : "outlined"}
                color={isPlacingField ? "secondary" : "primary"}
                startIcon={<AddIcon />}
                onClick={() => setIsPlacingField(!isPlacingField)}
                sx={{ mb: 2 }}
                fullWidth
              >
                {isPlacingField ? `Click on PDF to place on page ${currentPage}...` : 'Place New Field'}
              </Button>

              {isPlacingField && (
                <Notification type={{ style: 'info', icon: true }} style={{ marginBottom: 16 }}>
                  Click anywhere on the PDF to place a {getFieldTypeLabel(selectedFieldType)} field for {getSignerRoleLabel(selectedSignerRole)} on page {currentPage}.
                </Notification>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Field list */}
              <Typography variant="subtitle2" gutterBottom>
                Configured Fields ({fields.length})
              </Typography>
              
              <Box sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
                <List dense>
                  {fields.map((field, index) => (
                    <Box 
                      key={index}
                      ref={(el: HTMLDivElement | null) => {
                        if (el) {
                          fieldItemRefs.current.set(index, el);
                        } else {
                          fieldItemRefs.current.delete(index);
                        }
                      }}
                    >
                      <ListItem 
                        sx={{ 
                          borderLeft: `4px solid ${getFieldColor(field.fieldType)}`,
                          mb: editingFieldIndex === index ? 0 : 0.5,
                          backgroundColor: editingFieldIndex === index ? '#e3f2fd' : '#f5f5f5',
                          borderRadius: editingFieldIndex === index ? '0 4px 0 0' : '0 4px 4px 0',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleEditField(index)}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip 
                                label={getSignerRoleLabel(field.signerRole)} 
                                size="small"
                                sx={{ height: 20, fontSize: '10px' }}
                              />
                              <Typography variant="body2">
                                {getFieldTypeLabel(field.fieldType)}
                              </Typography>
                            </Box>
                          }
                          secondary={`Page ${field.pageNo + 1} | X: ${field.xCoord}, Y: ${field.yCoord} | ${field.absWidth}×${field.absHeight}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditField(index);
                            }}
                            color={editingFieldIndex === index ? "primary" : "default"}
                          >
                            {editingFieldIndex === index ? <ExpandLessIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                          </IconButton>
                          <IconButton 
                            edge="end" 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveField(index);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      
                      {/* Expandable edit form */}
                      <Collapse in={editingFieldIndex === index}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: '#e3f2fd', 
                          borderLeft: `4px solid ${getFieldColor(field.fieldType)}`,
                          borderRadius: '0 0 4px 0',
                          mb: 0.5 
                        }}>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <TextField
                                label="Page"
                                type="number"
                                value={(editFormData.pageNo ?? field.pageNo) + 1}
                                onChange={(e) => handleEditFormChange('pageNo', Math.max(0, (parseInt(e.target.value) || 1) - 1))}
                                size="small"
                                fullWidth
                                InputProps={{ inputProps: { min: 1 } }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Role</InputLabel>
                                <Select
                                  value={editFormData.signerRole ?? field.signerRole}
                                  label="Role"
                                  onChange={(e) => handleEditFormChange('signerRole', e.target.value)}
                                >
                                  {ESignSignerRoleOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="X Coord"
                                type="number"
                                value={editFormData.xCoord ?? field.xCoord}
                                onChange={(e) => handleEditFormChange('xCoord', parseInt(e.target.value) || 0)}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Y Coord"
                                type="number"
                                value={editFormData.yCoord ?? field.yCoord}
                                onChange={(e) => handleEditFormChange('yCoord', parseInt(e.target.value) || 0)}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Width"
                                type="number"
                                value={editFormData.absWidth ?? field.absWidth}
                                onChange={(e) => handleEditFormChange('absWidth', parseInt(e.target.value) || 0)}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Height"
                                type="number"
                                value={editFormData.absHeight ?? field.absHeight}
                                onChange={(e) => handleEditFormChange('absHeight', parseInt(e.target.value) || 0)}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Type</InputLabel>
                                <Select
                                  value={editFormData.fieldType ?? field.fieldType}
                                  label="Type"
                                  onChange={(e) => handleEditFormChange('fieldType', e.target.value)}
                                >
                                  {ESignFieldTypeOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={editFormData.isMandatory ?? field.isMandatory}
                                    onChange={(e) => handleEditFormChange('isMandatory', e.target.checked)}
                                    size="small"
                                  />
                                }
                                label="Required"
                                sx={{ mt: 0.5 }}
                              />
                            </Grid>
                            {(editFormData.fieldType ?? field.fieldType) === ESignFieldType.DateSigned && (
                              <Grid item xs={12}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label="Date Format"
                                  value={editFormData.dateFormat ?? field.dateFormat ?? 'MM/dd/yyyy'}
                                  onChange={(e) => handleEditFormChange('dateFormat', e.target.value)}
                                >
                                  {DATE_FORMAT_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                            )}
                            <Grid item xs={12}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                                Changes apply live - see the overlay update on the PDF
                              </Typography>
                              <Button size="small" variant="outlined" onClick={handleCancelEdit} fullWidth>
                                Done Editing
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
                  
                  {fields.length === 0 && (
                    <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                      No fields configured. Click "Place New Field" to add signature fields.
                    </Typography>
                  )}
                </List>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSaveFields}
          disabled={saving || fields.length === 0}
        >
          {saving ? 'Saving...' : 'Save Field Configuration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
